import React, { memo, useMemo } from 'react';
import { first } from 'lodash';

import { ScaleLinear } from 'd3-scale';

import { Bar, Line } from '@visx/shape';
import { GridColumns, GridRows } from '@visx/grid';
import { AxisLeft, AxisRight } from '@visx/axis';
import { getStringWidth } from '@visx/text';
import { common, model } from '@gosupersimple/types';

import { localPoint } from '@visx/event';

import { notNil } from '@/lib/utils';
import { ChartType } from '@/explore/types';
import { formatPropertyValue } from '@/explore/utils/format';
import { GroupFilter } from '@/explore/utils/drilldown';
import { InteractableProps } from '@/explore/utils/interactable';

import { ChartTooltip, ChartTooltipItem, detectHoveredSeries, renderGridColumns } from '../common';
import { CategoryData } from '../grouped-chart/types';
import { AreaStack } from './area-stack';
import { LineStack } from './line-stack';
import { BarStack } from './bar-stack';
import { StackValues } from './stack-values';
import {
  explodeCombinedKey,
  getLeftRightAxisFormats,
  getLeftRightAxisTypes,
  getSeriesAxes,
  isCombinedKey,
} from '../grouped-chart/utils';
import { SeriesValueDot } from '../timeseries-chart/series-value-dot';
import { CategoryScale, CategoryValue } from './types';
import { NonOverlappingValueLabels } from '../non-overlapping-value-labels';

import { getScreenPoint } from '../utils';

import { getBarSeriesPosition } from './vertical-category-chart';

import commonStyles, {
  gridLineColor,
  gridHighlightedLineColor,
  hoverLineColor,
} from '../charts.module.scss';

const NumYTicks = 7;

export type StackedChartData = {
  [key: string]: number | Date;
  date: Date;
}[];

export interface ChartColor {
  fill: string;
  highlight: string;
}

interface StackedCategoryChartProps {
  width: number;
  height: number;
  data: CategoryData;
  categories: CategoryValue[];
  categoryScale: CategoryScale;
  valueScales: {
    left: ScaleLinear<number, number>;
    right: ScaleLinear<number, number>;
  };
  highlightedCategory: CategoryValue | null;
  highlightedSeriesKey?: string | null;
  groupKey: string | undefined;
  onCategoryHighlight: (categoryValue: CategoryValue | null) => void;
  onCategoryClick?: (params: {
    x: number;
    y: number;
    key: string;
    groupFilters: GroupFilter[];
  }) => void;
  hideGrid: boolean;
  timezone: string;
  showTotals: boolean;
}

export const StackedCategoryChart = (props: StackedCategoryChartProps) => {
  const {
    width,
    height,
    data,
    categories,
    categoryScale,
    valueScales,
    highlightedCategory,
    highlightedSeriesKey,
    groupKey,
    onCategoryHighlight,
    onCategoryClick,
    hideGrid,
    timezone,
    showTotals,
  } = props;

  const hasOnlyBars = data.series.every(({ chartType }) => chartType === 'bar');
  const hasBars = hasOnlyBars || data.series.some(({ chartType }) => chartType === 'bar');
  const [tooltipData, setTooltipData] = React.useState<TooltipData | null>(null);

  const { axes, axisTypes, axisFormats } = useMemo(
    () => ({
      axes: getSeriesAxes(data.series),
      axisTypes: getLeftRightAxisTypes(data.series),
      axisFormats: getLeftRightAxisFormats(data.series),
    }),
    [data.series],
  );

  const formatLeftAxisValue = (n: number) =>
    formatPropertyValue(n, {
      type: axisTypes.left,
      format: axisFormats.left,
      style: 'compact',
    });

  const formatRightAxisValue = (n: number) =>
    formatPropertyValue(n, {
      type: axisTypes.right,
      format: axisFormats.right,
      style: 'compact',
    });

  const yAxisWidths = {
    left: Math.max(
      ...valueScales.left
        .ticks(NumYTicks)
        .map((i) => formatLeftAxisValue(i.valueOf()))
        .map((i) => getStringWidth(i) ?? 0),
    ),
  };

  const domainSizes = {
    left: valueScales.left.domain()[1] - valueScales.left.domain()[0],
    right: valueScales.right.domain()[1] - valueScales.right.domain()[0],
  };

  const hasRightAxisSeries = data.series.some((series) => series.axis === 'right');

  // Default to right as main axis in case no series on left axis
  const primaryValueScale = domainSizes.left > 0 ? valueScales.left : valueScales.right;

  // Force right axis ticks to align with primary axis ticks
  const rightAxisTicks = primaryValueScale
    .ticks(NumYTicks)
    .map((tick) => valueScales.right.invert(primaryValueScale(tick)));

  const handleMouseMove = (
    event: React.TouchEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>,
    categoryValue: CategoryValue,
  ) => {
    const point = localPoint(event);
    if (point === null) {
      return;
    }

    // Find the data item for this category
    const dataItem = data.items.find(
      ({ categoryValue: { formatted } }) => formatted === categoryValue.formatted,
    );

    if (dataItem === undefined) {
      setTooltipData(null);
      onCategoryHighlight(null);
      return;
    }

    const seriesData = data.series.map((s) => ({
      key: s.key,
      value: dataItem.values[s.key] ?? null,
      chartType: s.chartType,
      axis: s.axis,
    }));

    // Calculate bar positions for bar detection
    const barSeries = seriesData.filter((s) => s.chartType === 'bar');
    let barPositions: Array<{ key: string; x: number; width: number }> = [];

    if (barSeries.length > 0) {
      const categoryX = categoryScale(categoryValue.formatted) ?? 0;

      // For stacked bars, all segments share the same X position and width
      const { barWidth, x } = getBarSeriesPosition(categoryScale, 1, 0);
      barPositions = [
        {
          key: barSeries[0].key,
          x: categoryX + x,
          width: barWidth,
        },
      ];
    }

    // Detect which series is being hovered based on mouse Y position
    const hoveredSeriesKey = detectHoveredSeries(
      point.y,
      seriesData,
      valueScales,
      point.x,
      barPositions,
      true,
    );

    setTooltipData(
      buildTooltipData(
        categoryValue,
        getScreenPoint(point, event.target as SVGElement),
        data,
        timezone,
        hoveredSeriesKey,
      ),
    );

    onCategoryHighlight(categoryValue);
  };

  const handleMouseLeave = () => {
    setTooltipData(null);
    onCategoryHighlight(null);
  };

  const renderBarStacks = (chartData: CategoryData) => {
    const width = categoryScale.range()[1];
    const barGroupWidth = width / chartData.items.length;
    const barGroupInnerWidth = (width / chartData.items.length) * 0.7;
    const barWidth = barGroupInnerWidth / axes.length;
    const barInnerWidth = barWidth * 0.8;

    return axes.map((axis, idx) => {
      const offset = -(barGroupInnerWidth / 2) + idx * barWidth + barWidth / 2;
      return (
        <AxisBarStack
          key={`bar-stack-${axis}`}
          axis={axis}
          chartData={chartData}
          categoryScale={categoryScale}
          valueScale={valueScales[axis]}
          showTotals={showTotals}
          offset={offset}
          barGroupWidth={barGroupWidth}
          barWidth={barInnerWidth}
          highlightKey={highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey}
        />
      );
    });
  };

  const renderHighlightedPeriod = () => {
    if (!highlightedCategory) {
      return null;
    }

    const bandwidth = categoryScale.bandwidth();

    return (
      <rect
        x={categoryScale(highlightedCategory.formatted) ?? 0}
        y={0}
        width={bandwidth}
        height={height}
        fill={gridLineColor}
        pointerEvents="none"
      />
    );
  };

  const renderHighlightedCategory = () => {
    if (!highlightedCategory) {
      return null;
    }

    return (
      <>
        {hasOnlyBars ? null : (
          <Line
            from={{
              x:
                (categoryScale(highlightedCategory.formatted) ?? 0) + categoryScale.bandwidth() / 2,
              y: 0,
            }}
            to={{
              x:
                (categoryScale(highlightedCategory.formatted) ?? 0) + categoryScale.bandwidth() / 2,
              y: height,
            }}
            stroke={hoverLineColor}
            strokeWidth={1}
            pointerEvents="none"
            strokeDasharray="5,2"
          />
        )}
        {tooltipData !== null && isGroupedTooltipData(tooltipData)
          ? null
          : tooltipData?.points.map((point, i) => {
              if (point.chartType === 'bar') {
                return null;
              }

              const value = Number(
                tooltipData?.points
                  .slice(i)
                  .filter(({ axis }) => axis === point.axis)
                  .reduce(
                    (sum, p) => (point.chartType === p.chartType ? sum + (p.value ?? 0) : sum),
                    0,
                  ),
              );

              return (
                <circle
                  key={i}
                  cx={
                    (categoryScale(point.categoryValue.formatted) ?? 0) +
                    categoryScale.bandwidth() / 2
                  }
                  cy={valueScales[point.axis](value)}
                  r={5}
                  fill={point.color}
                  stroke="white"
                  strokeOpacity={0.2}
                  strokeWidth={1}
                  pointerEvents="none"
                />
              );
            })}
      </>
    );
  };

  const renderSeriesValueDots = () => {
    // Only render when there is a single date value
    if (data.items.length !== 1) {
      return null;
    }
    // Only render dots for line and area series
    const series = data.series
      .filter(({ chartType }) => chartType === 'line' || chartType === 'area')
      .reverse();
    return data.items.map((item) => {
      let cumValue = 0;
      return series.map(({ key, axis, color }) => {
        cumValue += item.values[key] ?? 0;
        return (
          <SeriesValueDot
            key={key}
            cx={categoryScale(item.categoryValue.formatted) ?? 0}
            cy={valueScales[axis](cumValue)}
            color={color}
          />
        );
      });
    });
  };

  const renderBandwidths = () =>
    categoryScale.domain().map((formattedCategoryValue) => {
      const categoryValue = categories.find((c) => c.formatted === formattedCategoryValue)!;
      return (
        <Bar
          {...InteractableProps}
          key={categoryValue.formatted}
          x={categoryScale(categoryValue.formatted) ?? 0}
          y={0}
          width={categoryScale.bandwidth()}
          height={height}
          fill={'transparent'}
          onMouseMove={(event) => handleMouseMove(event, categoryValue)}
          onMouseLeave={handleMouseLeave}
          onClick={(e) => {
            if (tooltipData === null || groupKey === undefined) {
              return;
            }
            const { clientX, clientY } = e;
            const series = data.series[0];
            onCategoryClick?.({
              x: clientX,
              y: clientY,
              key: isCombinedKey(series.key) ? explodeCombinedKey(series.key).valueKey : series.key,
              groupFilters: [
                {
                  key: groupKey,
                  value: categoryValue.raw,
                },
              ],
            });
          }}
        />
      );
    });

  return (
    <div className={commonStyles.graph}>
      <svg width={width} height={height} className={commonStyles.graphMain}>
        {hideGrid ? null : (
          <>
            <GridRows
              width={width}
              height={height}
              scale={primaryValueScale}
              stroke={gridLineColor}
              tickValues={primaryValueScale.ticks(NumYTicks)}
            />
            {hasOnlyBars ? null : (
              <>
                <GridColumns
                  width={width}
                  height={height}
                  scale={categoryScale}
                  stroke={gridLineColor}
                  // eslint-disable-next-line react/no-children-prop
                  children={(lines) =>
                    renderGridColumns(lines, { width, strokeColor: gridLineColor })
                  }
                />
                <GridColumns
                  width={width}
                  height={height}
                  scale={categoryScale}
                  stroke={gridHighlightedLineColor}
                  // eslint-disable-next-line react/no-children-prop
                  children={(lines) =>
                    renderGridColumns(lines, { width, strokeColor: gridHighlightedLineColor })
                  }
                />
              </>
            )}
          </>
        )}

        {axes.map((axis) => {
          const areaKeys = data.series
            .filter((s) => s.chartType === 'area' && s.axis === axis)
            .map(({ key }) => key);
          const lineKeys = data.series
            .filter((s) => s.chartType === 'line' && s.axis === axis)
            .map(({ key }) => key);

          return domainSizes[axis] <= 0 ? null : (
            <React.Fragment key={axis}>
              <AreaStack
                keys={areaKeys}
                data={data}
                valueScale={valueScales[axis]}
                categoryScale={categoryScale}
                highlightKey={highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey}
              />

              <LineStack
                keys={lineKeys}
                data={data}
                valueScale={valueScales[axis]}
                categoryScale={categoryScale}
                highlightKey={highlightedSeriesKey ?? tooltipData?.hoveredSeriesKey}
              />

              <NonOverlappingValueLabels>
                <StackValues
                  keys={areaKeys}
                  data={data}
                  valueScale={valueScales[axis]}
                  categoryScale={categoryScale}
                  showTotals={showTotals}
                />

                <StackValues
                  keys={lineKeys}
                  data={data}
                  valueScale={valueScales[axis]}
                  categoryScale={categoryScale}
                  showTotals={showTotals}
                />
              </NonOverlappingValueLabels>
            </React.Fragment>
          );
        })}

        {renderBarStacks(data)}
        {renderSeriesValueDots()}

        {hideGrid ? null : (
          <>
            {domainSizes.left > 0 && (
              <AxisLeft
                left={yAxisWidths.left + 4}
                scale={valueScales.left}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 4, dy: 10, textAnchor: 'end' })}
                tickValues={valueScales.left.ticks(NumYTicks)}
                tickFormat={(n) => formatLeftAxisValue(n.valueOf())}
              />
            )}
            {domainSizes.right > 0 && hasRightAxisSeries && (
              <AxisRight
                left={width - 4}
                scale={valueScales.right}
                hideTicks
                hideAxisLine
                tickClassName={commonStyles.tickLabel}
                tickLabelProps={() => ({ dx: 0, dy: 10, x: 0, textAnchor: 'end' })}
                tickValues={rightAxisTicks}
                tickFormat={(n) => formatRightAxisValue(n.valueOf())}
              />
            )}
          </>
        )}
      </svg>

      <svg width={width} height={height} className={commonStyles.graphInteractive}>
        {hasBars && renderHighlightedPeriod()}
        {renderHighlightedCategory()}
        {renderBandwidths()}
      </svg>

      {tooltipData && <Tooltip tooltipData={tooltipData} />}
    </div>
  );
};

const AxisBarStack = memo(function AxisBarStack(props: {
  axis: 'left' | 'right';
  chartData: CategoryData;
  categoryScale: CategoryScale;
  valueScale: ScaleLinear<number, number>;
  showTotals: boolean;
  offset: number;
  barGroupWidth: number;
  barWidth: number;
  highlightKey?: string;
}) {
  const {
    axis,
    chartData,
    categoryScale,
    valueScale,
    showTotals,
    offset,
    barGroupWidth,
    barWidth,
    highlightKey,
  } = props;

  const keys = useMemo(
    () =>
      chartData.series
        .filter((s) => s.chartType === 'bar' && s.axis === axis)
        .map(({ key }) => key),
    [axis, chartData.series],
  );

  const axisScale = useMemo(
    () =>
      categoryScale
        .copy()
        .range([categoryScale.range()[0] + offset, categoryScale.range()[1] + offset]),
    [categoryScale, offset],
  );

  return (
    <>
      <BarStack
        keys={keys}
        data={chartData}
        valueScale={valueScale}
        categoryScale={axisScale}
        offset={-(barGroupWidth / 2)}
        barWidth={barWidth}
        highlightKey={highlightKey}
      />
      <NonOverlappingValueLabels>
        <StackValues
          keys={keys}
          data={chartData}
          valueScale={valueScale}
          categoryScale={axisScale}
          bar
          showTotals={showTotals}
        />
      </NonOverlappingValueLabels>
    </>
  );
});

type TooltipData = {
  categoryLabel: string;
  categoryValue: CategoryValue;
  position: { x: number; y: number };
  timezone: string;
  hoveredSeriesKey?: string;
  points: {
    label: string;
    value: number | null;
    categoryValue: CategoryValue;
    type: model.PropertyType | null;
    format?: common.PropertyValueFormat;
    color: string;
    axis: 'left' | 'right';
    chartType: ChartType;
    isHovered?: boolean;
  }[];
};

type GroupedTooltipData = {
  date: Date;
  position: { x: number; y: number };
  timezone: string;
  groups: {
    label: string;
    points: {
      label: string;
      value: number | null;
      type: model.PropertyType | null;
      format?: common.PropertyValueFormat;
      date: Date;
      color: string;
      axis: 'left' | 'right';
      chartType: ChartType;
    }[];
  }[];
};

const isGroupedTooltipData = (data: TooltipData | GroupedTooltipData): data is GroupedTooltipData =>
  'groups' in data;

const Tooltip = ({ tooltipData }: { tooltipData: TooltipData }) => {
  if (tooltipData.points.length === 0) {
    return null;
  }

  const totalValueType = first(tooltipData.points)?.type ?? 'Number';
  const totalValueFormat = tooltipData.points.every(
    (point) => point.format === first(tooltipData.points)?.format,
  )
    ? first(tooltipData.points)?.format
    : undefined;

  return (
    <ChartTooltip
      position={tooltipData.position}
      title={`${tooltipData.categoryLabel} ${tooltipData.categoryValue.formatted}`}>
      <ul>
        {tooltipData.points.length > 1 && (
          <li>
            Total:{' '}
            <strong>
              {formatPropertyValue(
                tooltipData.points
                  .map((point) => point.value)
                  .filter(notNil)
                  .reduce((sum, value) => sum + value, 0),
                { type: totalValueType, format: totalValueFormat },
              )}
            </strong>
          </li>
        )}
        {tooltipData.points.map((point, i) => (
          <ChartTooltipItem
            key={i}
            label={point.label ?? '-'}
            value={formatPropertyValue(point.value, { type: point.type, format: point.format })}
            color={point.color}
            isHovered={point.isHovered}
          />
        ))}
      </ul>
    </ChartTooltip>
  );
};

const buildTooltipData = (
  categoryValue: CategoryValue,
  position: { x: number; y: number },
  data: CategoryData,
  timezone: string,
  hoveredSeriesKey?: string,
): TooltipData | null => {
  const item = data.items.find((item) => item.categoryValue.formatted === categoryValue.formatted);

  if (item === undefined) {
    return null;
  }

  const points = data.series.map(({ key, label, type, format, axis, chartType, color }) => ({
    label,
    value: item.values[key] ?? null,
    type,
    format,
    categoryValue: item.categoryValue,
    color,
    axis,
    chartType,
    isHovered: hoveredSeriesKey === key,
  }));

  return {
    categoryLabel: item.label,
    categoryValue,
    position,
    timezone,
    points,
    hoveredSeriesKey,
  };
};
